import { getHttpClient } from '@/singleton-clients';
import { nanoidUUIDv4 } from '@/utils/id-utils';
import { resolveTrustedUrl } from '@/utils/trusted-base-url';

type CreateElasticubeProps = {
	title: string;
};

type CreateElasticubeResponse = {
	data: {
		newCube: {
			oid: string;
		};
	};
	errors: {
		error: {
			message: string;
		};
	}[];
};

type GetCSVSchemaDetailsProps = {
	filePath: string;
	fileName: string;
};

type GetCSVSchemaDetailsResponse = {
	connection: {
		ApiVersion: number;
		files: string[];
		delimiter: string;
		hasHeader: boolean;
		ignoreMissingColumns: boolean;
		excludeIfBegins: string;
		excludeIfContains: string;
		stringQuote: string;
		ignoreFirstRows: string;
		culture: string;
		columnsDateTimePatterns: string;
	};
	schema: string;
	table: string;
};

type GetPerspectiveOidProps = {
	elasticubeOid: string;
};

type PerspectiveResponse = {
	lastUpdated: string;
	title: string;
	server: string;
	datasets: any[];
	oid: string;
	'modeling-transformations': any[];
	relations: any[];
	shares: Array<{
		partyId: string;
		type: string;
		permission: string;
	}>;
	type: string;
	relationType: string;
	experiments: Record<string, any>;
	fiscal: string;
	_id: string;
	analyticalEngine: Record<string, any>;
	buildDestination: {
		destination: string;
	};
	creator: string;
	hasDatasetsWithoutConnectionParameters: boolean | null;
	modelStatistics: {
		enableModelStatistics: boolean;
		allTablesPerCollection: boolean;
		maxTablesPerCollection: number;
	};
	tenantId: string;
	relationsTables: any[];
	datamodelId: string;
};

type GetPerspectiveOidResponse = {
	perspectives: PerspectiveResponse[];
	revisions: any[];
};

type AddDataSetProps = {
	elasticubeOid: string;
	filePath: string;
	fileName: string;
	table: any;
	perspectiveOid: string;
};

type AddDataSetResponse = {
	dataSet: {
		oid: string;
	};
	columns: any[];
	tableOid: string;
};

type BuildElasticubeProps = {
	elasticubeOid: string;
};

type BuildElasticubeResponse = {
	buildElasticube: {
		oid: string;
	};
};

export type BuildStatus =
	| 'building'
	| 'pending'
	| 'done'
	| 'failed'
	| 'cancelled';

export type BuildStatusResponse = {
	oid: string;
	datamodelId: string;
	buildType: string;
	status: BuildStatus;
	datamodelTitle: string;
	datamodelType: string;
	indexSize: number;
	instanceId: string;
	schemaLastUpdate: string;
	created: string;
	started: string;
	completed: string;
	rowLimit: number;
	source: string;
};

type AddRelationProps = {
	elasticubeOid: string;
	source: any;
	target: any;
};

type AddRelationResponse = {
	relation: {
		oid: string;
	};
};

export type GetElasticubesResponse = {
	data: {
		elasticubesMetadata: Array<{
			oid: string;
			title: string;
			server: string;
			tenant: {
				_id: string;
				name: string;
				systemManagement: boolean;
				__typename: string;
			};
			fiscal: string;
			serverId: string;
			hasDatasets: boolean;
			type: string;
			relationType: string;
			provider: string;
			importTime: string;
			hasDatasetsWithoutConnectionParameters: boolean;
			datasets: Array<{
				oid: string;
				database: string;
				schemaName: string;
				connection: {
					id: string;
					__typename: string;
				};
				__typename: string;
			}>;
			set: {
				title: string;
				__typename: string;
			};
			status: string;
			lastBuildTime: string;
			lastBuildStatus: string;
			lastSuccessfulBuildStartTime: string;
			lastSuccessfulManualBuildStartTime: string;
			lastSuccessfulBuildTime: string;
			lastPublishTime: string;
			lastUpdated: string;
			hasPendingChanges: boolean;
			nextBuildTime: string;
			creator: {
				id: string;
				firstName: string;
				lastName: string;
				__typename: string;
			};
			sizeInMb: number;
			shares: Array<{
				partyId: string;
				type: string;
				permission: string;
				firstName?: string;
				lastName?: string;
				email?: string;
				name?: string;
				ad?: boolean;
				objectSid?: string;
				everyone?: boolean;
				tenantEveryone?: boolean;
				admins?: boolean;
				__typename: string;
			}>;
			buildDestination: {
				destination: string;
				database: string;
				schema: string;
				resultLimit: number;
				queryTimeout: number;
				__typename: string;
			};
			modelStatistics: {
				enableModelStatistics: boolean;
				allTablesPerCollection: boolean;
				maxTablesPerCollection: number;
				__typename: string;
			};
			experiments: Array<{
				__typename: string;
			}>;
			acceleration: {
				parentModelOid: string;
				__typename: string;
			};
			analyticalEngine: {
				queryAeMode: string;
				customTranslationMode: string;
				generateDependenciesMode: string;
				__typename: string;
			};
			__typename: string;
		}>;
	};
};

type GetElasticubeProps = {
	cubeOid: string;
};

export type GetElasticubeResponse = {
	elasticube: {
		oid: string;
		title: string;
		server: string;
		tenant: {
			_id: string;
			name: string;
			systemManagement: boolean;
		};
		fiscal: string;
		type: string;
		relationType: string;
		enableDimensionalModeling: boolean;
		lastSuccessfulManualBuildStartTime: string;
		lastBuildTime: string;
		lastPublishTime: string;
		hasDatasetsWithoutConnectionParameters: boolean;
		datasets: {
			oid: string;
			name: string;
			database: string;
			schemaName: string;
			lastUpdated: string;
			type: string;
			liveQuerySettings: {
				timeout: number;
				autoRefresh: boolean;
				refreshRate: number;
				resultLimit: number;
				__typename: string;
			};
			connection: {
				id: string;
				oid: string;
				provider: string;
				schema: string;
				fileName: string;
				parameters: string;
				protectedParameters: string;
				globalTableConfigOptions: string;
				__typename: string;
			};
			schema: {
				tables: {
					oid: string;
					id: string;
					name: string;
					role: string;
					isRoleBySystem: boolean;
					description: string;
					type: string;
					tags: string[];
					hidden: boolean;
					lastUpdated: string;
					configOptions: string;
					schemaName: string;
					isMaterialized: boolean;
					buildBehavior: {
						type: string;
						accumulativeConfig: {
							type: string;
							column: string;
							lastDays: number;
							keepOnlyDays: number;
							__typename: string;
						};
						__typename: string;
					};
					columns: {
						oid: string;
						id: string;
						name: string;
						role: string;
						isRoleBySystem: boolean;
						description: string;
						type: string;
						size: number;
						precision: number;
						scale: number;
						hidden: boolean;
						indexed: boolean;
						isUpsertBy: boolean;
						__typename: string;
					};
					expression: {
						oid: string;
						lastUpdated: string;
						expression: string;
						isDatabaseDialect: boolean;
						__typename: string;
					};
					customCode: {
						noteBookId: string;
						dependsOn: string[];
						additionalParameters: string;
						codePath: string;
						cellsDisable: boolean;
						language: string;
						mode: string;
						serverUrl: string;
						timeout: number;
						__typename: string;
					};
					tupleTransformations: {
						type: string;
						arguments: string[];
						__typename: string;
					};
					__typename: string;
				};
				__typename: string;
			};
			__typename: string;
		};
		relationsTables: {
			firstTable: string;
			secondTable: string;
			directionType: string;
			condition: string;
			__typename: string;
		};
		relations: {
			oid: string;
			columns: {
				dataset: string;
				table: string;
				column: string;
				isDropped: boolean;
				__typename: string;
			}[];
			__typename: string;
		};
		experiments: {
			__typename: string;
		}[];
		buildDestination: {
			destination: string;
			database: string;
			schema: string;
			__typename: string;
		};
		modelStatistics: {
			enableModelStatistics: boolean;
			allTablesPerCollection: boolean;
			maxTablesPerCollection: number;
			recollectStatsInterval: number;
			__typename: string;
		};
		__typename: string;
	};
};

type DeleteElasticubeProps = {
	oid: string;
	replacementDataSource?: {
		type: string;
		oid: string;
	};
};

type DeleteElasticubeResponse = {
	data: {
		removeElasticubeByOid: boolean;
	};
};

type UploadCSVResponse = Array<{
	filename: string;
	extension: string;
	size: number;
	type: string;
	storageInfo: {
		path: string;
	};
	uploader: string;
	source: string;
	createdAt: string;
	tenantId: string;
	_id: string;
}>;

export async function createElasticube({ title }: CreateElasticubeProps) {
	const httpClient = getHttpClient();
	const body = {
		operationName: 'createNewElasticube',
		variables: {
			title,
			type: 'extract',
			relationType: 'direct',
			experiments: {},
			buildDestination: {
				destination: 'elasticube',
			},
		},
		query: 'mutation createNewElasticube($title: String!, $type: ElasticubeType, $relationType: RelationType, $experiments: ExperimentsInput, $buildDestination: BuildDestinationInput, $fiscal: String) {\n  newCube: createNewElasticube(\n    title: $title\n    type: $type\n    relationType: $relationType\n    experiments: $experiments\n    buildDestination: $buildDestination\n    fiscal: $fiscal\n  ) {\n    oid\n    title\n    server\n    type\n    relationType\n    lastBuildTime\n    lastUpdated\n    fiscal\n    creator {\n      id\n      firstName\n      lastName\n      __typename\n    }\n    shares {\n      partyId\n      type\n      ... on ShareUserInfo {\n        firstName\n        lastName\n        email\n        __typename\n      }\n      ... on ShareGroupInfo {\n        name\n        ad\n        objectSid\n        everyone\n        tenantEveryone\n        admins\n        __typename\n      }\n      __typename\n    }\n    experiments {\n      ...experiments\n      __typename\n    }\n    buildDestination {\n      destination\n      __typename\n    }\n    __typename\n  }\n}\n\nfragment experiments on Experiments {\n  __typename\n}\n',
	};
	let data: CreateElasticubeResponse | undefined;
	try {
		data = await httpClient.post<CreateElasticubeResponse>(
			`/api/v2/ecm/`,
			body,
		);
	} catch (err: any) {
		console.error(err);
		throw new Error(err.message);
	}
	if (data?.errors) {
		throw new Error(data.errors[0].error.message);
	}
	return data?.data?.newCube;
}

export async function getCSVSchemaDetails({
	filePath,
	fileName,
}: GetCSVSchemaDetailsProps): Promise<GetCSVSchemaDetailsResponse | null> {
	const body = {
		connection: {
			ApiVersion: 2,
			files: [filePath],
			delimiter: ',',
			hasHeader: true,
			ignoreMissingColumns: false,
			excludeIfBegins: '',
			excludeIfContains: '',
			stringQuote: '"',
			ignoreFirstRows: '',
			culture: 'en-US',
			columnsDateTimePatterns: '{}',
			fileNames: [fileName],
		},
		schema: filePath,
		table: filePath.split('/').pop(),
	};

	const httpClient = getHttpClient();
	const response = await httpClient.post<GetCSVSchemaDetailsResponse>(
		`/api/v1/live_connectors/CSV/table_schema_details`,
		body,
	);

	return response ?? null;
}

export async function getPerspectiveOid({
	elasticubeOid,
}: GetPerspectiveOidProps): Promise<GetPerspectiveOidResponse | null> {
	const httpClient = getHttpClient();
	const response = await httpClient.get<GetPerspectiveOidResponse>(
		`/api/v2/perspectives/getPerspectiveElasticubesAndLatestRevisionsByOid?parentOid=${elasticubeOid}`,
	);

	return response ?? null;
}

export async function addDataSet({
	elasticubeOid,
	filePath,
	fileName,
	table,
	perspectiveOid,
}: AddDataSetProps) {
	const tableId = filePath.split('/').pop();
	const oid = tableId?.replace('.csv', '') || '';
	const name = fileName.replace('.csv', '');
	const { schemaName, columns } = table;
	const dataColumns = columns.map((column: any) => ({
		name: column.columnName,
		type: column.dbType,
		oid: nanoidUUIDv4(),
		size: column.size,
		precision: column.precision,
		scale: column.scale,
		hidden: false,
		indexed: true,
		id: column.columnName,
	}));

	const body = {
		operationName: 'addDataset',
		variables: {
			elasticubeOid,
			connection: {
				provider: 'CSV',
				schema: filePath,
				parameters: {
					ApiVersion: 2,
					files: [filePath],
				},
				uiParams: {},
				globalTableConfigOptions: {
					datesPicker: {},
				},
				fileName,
			},
			tables: [
				{
					id: tableId,
					name,
					oid,
					hidden: false,
					schemaName,
					buildBehavior: {
						type: 'sync',
					},
					columns: dataColumns,
					configOptions: {
						delimiter: ',',
						hasHeader: true,
						ignoreMissingColumns: false,
						excludeIfBegins: '',
						excludeIfContains: '',
						stringQuote: '"',
						ignoreFirstRows: '',
						culture: 'en-US',
						columnsDateTimePatterns: '{}',
					},
					tupleTransformations: [],
				},
			],
			suggestedOid: nanoidUUIDv4(),
			schemaName,
			perspectiveOid,
			updatedPerspectiveData: {
				relationsTables: [],
			},
		},
		query: `mutation addDataset($elasticubeOid: UUID!, $connection: ConnectionInput!, $tables: [TableInput!]!, $name: String, $suggestedOid: UUID!, $database: String, $schemaName: String, $perspectiveOid: UUID!, $updatedPerspectiveData: PerspectiveInput) {
  markTransactional(elasticubeOid: $elasticubeOid)
  dataset: addDatasetToElasticube(
    elasticubeOid: $elasticubeOid
    connection: $connection
    tables: $tables
    name: $name
    suggestedOid: $suggestedOid
    database: $database
    schemaName: $schemaName
  ) {
    oid
    connection {
      oid
      protectedParameters
      __typename
    }
    __typename
  }
  updatePerspective(
    perspectiveOid: $perspectiveOid
    updatedPerspectiveData: $updatedPerspectiveData
  )
}`,
	};

	const httpClient = getHttpClient();
	const data = await httpClient.post<AddDataSetResponse>(
		`/api/v2/ecm/`,
		body,
	);
	return { dataSet: data, columns: dataColumns, tableOid: oid };
}

export async function buildElasticube({
	elasticubeOid,
}: BuildElasticubeProps): Promise<BuildElasticubeResponse | null> {
	const body = {
		operationName: 'buildElasticube',
		variables: {
			elasticubeOid,
			buildType: 'restart',
		},
		query: 'mutation buildElasticube($elasticubeOid: UUID!, $buildType: ElasticubeBuildType, $buildRows: Int) {\n  buildElasticube(\n    elasticubeOid: $elasticubeOid\n    type: $buildType\n    rows: $buildRows\n  )\n}\n',
	};
	const httpClient = getHttpClient();
	const response = await httpClient.post<BuildElasticubeResponse>(
		`/api/v2/ecm/`,
		body,
	);
	return response ?? null;
}

export async function buildStatus(): Promise<BuildStatusResponse[] | null> {
	const httpClient = getHttpClient();
	const response = await httpClient.get<BuildStatusResponse[]>(
		`/api/v2/builds`,
	);
	return response ?? null;
}

export async function addRelation({
	elasticubeOid,
	source,
	target,
}: AddRelationProps): Promise<AddRelationResponse | null> {
	const body = {
		operationName: 'addRelation',
		variables: {
			elasticubeOid,
			source,
			target,
			suggestedOid: nanoidUUIDv4(),
		},
		query: 'mutation addRelation($elasticubeOid: UUID!, $source: RelationReferenceInput!, $target: RelationReferenceInput!, $suggestedOid: UUID!) {\n  relation: addRelation(\n    elasticubeOid: $elasticubeOid\n    source: $source\n    target: $target\n    suggestedOid: $suggestedOid\n  ) {\n    oid\n    columns {\n      column\n      __typename\n    }\n    __typename\n  }\n}\n',
	};
	const httpClient = getHttpClient();
	const response = await httpClient.post<AddRelationResponse>(
		`/api/v2/ecm/`,
		body,
	);
	return response ?? null;
}

export async function getElasticubes() {
	const httpClient = getHttpClient();
	const body = {
		operationName: 'elasticubesMetadata',
		variables: { isViewMode: false },
		query: 'query elasticubesMetadata($tenantFilter: String, $isViewMode: Boolean) {\n  elasticubesMetadata(tenantFilter: $tenantFilter, isViewMode: $isViewMode) {\n    ...ecMetaData\n    __typename\n  }\n}\n\nfragment ecMetaData on ElasticubeMetadata {\n  oid\n  title\n  server\n  tenant {\n    _id\n    name\n    systemManagement\n    __typename\n  }\n  fiscal\n  serverId\n  hasDatasets\n  type\n  relationType\n  provider\n  importTime\n  hasDatasetsWithoutConnectionParameters\n  datasets {\n    oid\n    database\n    schemaName\n    connection {\n      id\n      __typename\n    }\n    __typename\n  }\n  set {\n    title\n    __typename\n  }\n  status\n  lastBuildTime\n  lastBuildStatus\n  lastSuccessfulBuildStartTime\n  lastSuccessfulManualBuildStartTime\n  lastSuccessfulBuildTime\n  lastPublishTime\n  lastUpdated\n  hasPendingChanges\n  nextBuildTime\n  creator {\n    id\n    firstName\n    lastName\n    __typename\n  }\n  sizeInMb\n  shares {\n    partyId\n    type\n    permission\n    ... on ShareUserInfo {\n      firstName\n      lastName\n      email\n      __typename\n    }\n    ... on ShareGroupInfo {\n      name\n      ad\n      objectSid\n      everyone\n      tenantEveryone\n      admins\n      __typename\n    }\n    __typename\n  }\n  buildDestination {\n    destination\n    database\n    schema\n    resultLimit\n    queryTimeout\n    __typename\n  }\n  modelStatistics {\n    enableModelStatistics\n    allTablesPerCollection\n    maxTablesPerCollection\n    __typename\n  }\n  experiments {\n    ...experiments\n    __typename\n  }\n  acceleration {\n    parentModelOid\n    __typename\n  }\n  analyticalEngine {\n    queryAeMode\n    customTranslationMode\n    generateDependenciesMode\n    __typename\n  }\n  __typename\n}\n\nfragment experiments on Experiments {\n  __typename\n}\n',
	};

	const response = await httpClient.post<GetElasticubesResponse>(
		`/api/v2/ecm/`,
		body,
	);
	if (!response?.data?.elasticubesMetadata) {
		throw new Error('Failed to get elasticubes list');
	}
	return response.data.elasticubesMetadata;
}

export async function getElasticube({
	cubeOid,
}: GetElasticubeProps): Promise<GetElasticubeResponse | null> {
	const httpClient = getHttpClient();
	const body = {
		operationName: 'elasticubeByOid',
		variables: { cubeOid },
		query: 'query elasticubeByOid($cubeOid: UUID!) {\n  elasticube: elasticubeByOid(oid: $cubeOid) {\n    ...ecData\n    __typename\n  }\n}\n\nfragment ecData on Elasticube {\n  oid\n  title\n  server\n  tenant {\n    _id\n    name\n    systemManagement\n    __typename\n  }\n  fiscal\n  type\n  relationType\n  enableDimensionalModeling\n  lastSuccessfulManualBuildStartTime\n  lastBuildTime\n  lastPublishTime\n  hasDatasetsWithoutConnectionParameters\n  datasets {\n    oid\n    name\n    database\n    schemaName\n    lastUpdated\n    type\n    liveQuerySettings {\n      timeout\n      autoRefresh\n      refreshRate\n      resultLimit\n      __typename\n    }\n    connection {\n      id\n      oid\n      provider\n      schema\n      fileName\n      parameters\n      protectedParameters\n      globalTableConfigOptions\n      __typename\n    }\n    schema {\n      tables {\n        oid\n        id\n        name\n        role\n        isRoleBySystem\n        description\n        type\n        tags\n        hidden\n        lastUpdated\n        configOptions\n        schemaName\n        isMaterialized\n        buildBehavior {\n          type\n          accumulativeConfig {\n            type\n            column\n            lastDays\n            keepOnlyDays\n            __typename\n          }\n          __typename\n        }\n        columns {\n          oid\n          id\n          name\n          role\n          isRoleBySystem\n          description\n          type\n          size\n          precision\n          scale\n          hidden\n          indexed\n          isUpsertBy\n          __typename\n        }\n        expression {\n          oid\n          lastUpdated\n          expression\n          isDatabaseDialect\n          __typename\n        }\n        customCode {\n          noteBookId\n          dependsOn\n          additionalParameters\n          codePath\n          cellsDisable\n          language\n          mode\n          serverUrl\n          timeout\n          __typename\n        }\n        tupleTransformations {\n          type\n          arguments\n          __typename\n        }\n        __typename\n      }\n      __typename\n    }\n    __typename\n  }\n  relationsTables {\n    firstTable\n    secondTable\n    directionType\n    condition\n    __typename\n  }\n  relations {\n    oid\n    columns {\n      dataset\n      table\n      column\n      isDropped\n      __typename\n    }\n    __typename\n  }\n  experiments {\n    ...experiments\n    __typename\n  }\n  buildDestination {\n    destination\n    database\n    schema\n    __typename\n  }\n  modelStatistics {\n    enableModelStatistics\n    allTablesPerCollection\n    maxTablesPerCollection\n    recollectStatsInterval\n    __typename\n  }\n  __typename\n}\n\nfragment experiments on Experiments {\n  __typename\n}\n',
	};

	const response = await httpClient.post<GetElasticubeResponse>(
		`/api/v2/ecm/`,
		body,
	);
	return response ?? null;
}

export async function deleteElasticube({
	oid,
	replacementDataSource,
}: DeleteElasticubeProps): Promise<boolean> {
	const body = {
		operationName: 'removeElasticubeByOid',
		variables: {
			oid,
			replacementDataSource,
		},
		query: 'mutation removeElasticubeByOid($oid: UUID!, $replacementDataSource: DataSourceInput) {\n  removeElasticubeByOid(oid: $oid, replacementDataSource: $replacementDataSource)\n}\n',
	};

	const httpClient = getHttpClient();
	const response = await httpClient.post<DeleteElasticubeResponse>(
		`/api/v2/ecm/`,
		body,
	);
	if (!response?.data) {
		throw new Error('Failed to delete elasticube');
	}
	return response.data.removeElasticubeByOid;
}

async function validateFile(
	fileName: string,
	csv: string,
): Promise<{ file: File; fileToken: string }> {
	const httpClient = getHttpClient();
	const file = new File([csv], fileName, { type: 'text/csv' });
	const payload = {
		filename: fileName,
		size: file.size,
	};

	const response = await httpClient.post<{ token: string }>(
		'/storage/fs/validate_file',
		payload,
	);

	if (!response?.token) {
		throw new Error('Failed to get file validation token');
	}

	return { file, fileToken: response.token };
}

export async function uploadCSVMultipart(
	fileName: string,
	csvContent: string,
): Promise<UploadCSVResponse> {
	const httpClient = getHttpClient();
	const { fileToken, file } = await validateFile(fileName, csvContent);

	const formData = new FormData();
	formData.append('file', file);

	const headers: Record<string, string> = {
		Accept: 'application/json',
		Internal: 'true',
		'x-upload-token': fileToken,
	};
	httpClient.auth.applyHeaders(headers);

	const uploadUrl = resolveTrustedUrl(
		httpClient.url,
		'/storage/fs/upload',
	).toString();

	const response = await fetch(uploadUrl, {
		method: 'POST',
		headers,
		body: formData,
		credentials: 'include',
	});

	if (!response.ok) {
		if (response.status === 403) {
			throw new Error(
				"Sorry, file upload is not available for this instance as it's behind Cloudflare and we don't have a proper API for connection file upload",
			);
		}
		throw new Error('Failed to upload file');
	}

	const data = await response.json();
	return data;
}
